import {
	Button,
	ChevronDown,
	Popover,
	PopoverContent,
	PopoverItem,
	PopoverScrollArea,
	PopoverSearch,
	PopoverTrigger,
} from "@ondit/ui";
import { Check } from "lucide-react";
import { useMemo, useState } from "react";

interface SearchOption {
	value: string;
	label: string;
	description?: string;
	keywords?: string;
}

/**
 * One of a long list, found by typing.
 *
 * The filtering is this component's rather than the popover's because an option
 * matches on three fields — its label, its description and whatever else the
 * caller knows it by — and the popover only knows what it renders.
 */
export function SearchSelect({
	value,
	options,
	onChange,
	label,
	placeholder,
	searchLabel,
	emptyLabel,
	disabled,
}: {
	value: string | null;
	options: SearchOption[];
	onChange: (value: string) => void;
	label: string;
	placeholder: string;
	searchLabel: string;
	emptyLabel: string;
	disabled?: boolean;
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const selected = options.find((option) => option.value === value);
	const shown = useMemo(() => {
		const needle = query.trim().toLowerCase();
		if (!needle) return options;
		return options.filter((option) =>
			`${option.label} ${option.description ?? ""} ${option.keywords ?? ""}`.toLowerCase().includes(needle),
		);
	}, [options, query]);

	return (
		<Popover
			open={open && !disabled}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) setQuery("");
			}}
		>
			<PopoverTrigger asChild>
				<Button
					variant="default"
					className="w-full min-w-0 justify-between"
					disabled={disabled}
					aria-label={`${label}: ${selected?.label ?? placeholder}`}
					aria-expanded={open}
				>
					<span className="truncate">{selected?.label ?? placeholder}</span>
					<ChevronDown aria-hidden className="size-[14px]" />
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" minWidth={280} className="p-0">
				<PopoverSearch placeholder={searchLabel} onValueChange={setQuery} />
				<PopoverScrollArea>
					{shown.length === 0 ? (
						<p className="py-6 text-center text-sm text-[var(--text-muted)]">{emptyLabel}</p>
					) : (
						shown.map((option) => (
							<PopoverItem
								key={option.value}
								active={value === option.value}
								onClick={() => {
									onChange(option.value);
									setOpen(false);
								}}
							>
								<span className="min-w-0 flex-1">
									<span className="block font-medium">{option.label}</span>
									{option.description && <span className="block text-[var(--text-muted)]">{option.description}</span>}
								</span>
								{value === option.value && <Check aria-hidden className="size-4 shrink-0" />}
							</PopoverItem>
						))
					)}
				</PopoverScrollArea>
			</PopoverContent>
		</Popover>
	);
}
